import React, { useEffect, useState } from 'react';

import { format, getZonedDate } from '@eika-infrastruktur/date';
import { formatAccountNumber } from '@eika-infrastruktur/format';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';
import { LinkButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { AttentionLabel } from '@eika/label';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import type { Currency, CurrencyExchangeResult } from '../../../../generated-types';
import { usePaymentAccountContext } from '../../../../hooks/useAccountState';
import useBankCountry from '../../../../hooks/useBankCountry';
import useTranslation, { formatJsx } from '../../../../hooks/useTranslation';
import { getCounterValue } from '../../../../services/currencyService';
import CurrencyExchangeDescription from '../CurrencyExchangeDescription';
import useFormContext from '../form/useFormContext';
import SummaryDescription from './ListElements/SummaryDescription';

import './Summary.scss';

const Summary: React.FC = () => {
    const translated = useTranslation();
    const [openExpandable, setOpenExpandable] = useState(false);
    const [currencyExchangeResult, setCurrencyExchangeResult] = useState<CurrencyExchangeResult>();
    const [currencyExchangeError, setCurrencyExchangeError] = useState<RequestExceptionWithResponse>();
    const toCurrency: Currency = 'NOK';

    const { state } = useFormContext();
    const { targetFromAccount } = usePaymentAccountContext();

    const date = state.paymentDate || getZonedDate();

    const bankCountry = useBankCountry(state.bankValidation?.bank?.countryCode);

    useEffect(() => {
        if (state.currency && state.amount) {
            getCounterValue(state.currency, toCurrency, state.amount)
                .then((exchangeResult) => setCurrencyExchangeResult(exchangeResult))
                .catch((error) => setCurrencyExchangeError(error));
        }
    }, [state.currency, state.amount]);

    return (
        <section className="summary-card" aria-labelledby="cross-border-payment-summary-card">
            <RichIcon type={RichIconTypes.JORDKLODE} className="summary-card__image" />
            <div className="summary-card__accounts-header">
                <p>
                    {formatJsx(
                        translated.crossBorderPayment.summary.payFrom,
                        <strong>{targetFromAccount?.alias} </strong>,
                    )}
                    {formatAccountNumber(state.fromAccount)}
                    {formatJsx(translated.crossBorderPayment.summary.payTo, <strong>{state.receiver?.name} </strong>)}
                    {formatJsx(
                        translated.crossBorderPayment.summary.payToCountry,
                        <strong>{bankCountry?.name}</strong>,
                        <>{state.receiver?.accountNumber}</>,
                    )}
                </p>
            </div>
            <AttentionLabel className="summary-card__payment-date" text={format(date, 'dd MMMM')} />
            <span className="summary-card__amount">{`${state.amount} ${
                state.currency ?? state.country?.currency ?? 'EUR'
            }`}</span>
            {!currencyExchangeError && (
                <div className="summary-card__currency-exchange">
                    <CurrencyExchangeDescription
                        label={translated.crossBorderPayment.calculatedExchangeRate}
                        value={currencyExchangeResult?.amount ?? 0}
                    />
                </div>
            )}
            <span className="summary-card__payment-description">{`"${state.messageReceiver}"`}</span>
            <LinkButton
                icon={openExpandable ? IconTypes.LITEN_PIL_OPP : IconTypes.LITEN_PIL_NED}
                iconOnRightSide
                onClick={() => setOpenExpandable(!openExpandable)}
                className="summary-card__expandable-button"
            >
                {openExpandable
                    ? translated.crossBorderPayment.summary.closeSummary
                    : translated.crossBorderPayment.summary.showSummary}
            </LinkButton>
            {openExpandable && <SummaryDescription />}
        </section>
    );
};

export default Summary;
